import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

// Reusable bottom sheet (PRD D14 / prototype .sheet-overlay pattern —
// docs/prototypes/journey-detail): the standard container for modal-ish mobile
// surfaces (stop/leg detail views). A dimmed overlay with a card that slides up
// from the bottom edge: drag handle, close ×, scrollable body, and an optional
// sticky actions bar (e.g. Save/Discard) pinned above the bottom edge.
//
// Behavior: closes on overlay tap and Escape; locks page scroll while open;
// animates in/out (the component stays mounted briefly after `open` flips false
// so the slide-down can play). Sits BELOW ConfirmationDialog (z 3000) so
// confirms triggered from inside a sheet appear above it.

export interface BottomSheetProps {
  open: boolean;
  onClose: () => void;
  /** Accessible name for the dialog. */
  ariaLabel: string;
  children: React.ReactNode;
  /** Sticky bottom action bar; rendered only when provided (prototype .sheet-actions). */
  actions?: React.ReactNode;
}

const ANIM_MS = 280;

const BottomSheet: React.FC<BottomSheetProps> = ({ open, onClose, ariaLabel, children, actions }) => {
  const [mounted, setMounted] = useState(open);
  const [shown, setShown] = useState(false);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (open) {
      if (closeTimer.current) { clearTimeout(closeTimer.current); closeTimer.current = null; }
      setMounted(true);
      // Two frames so the closed position paints before the transition starts.
      const raf = requestAnimationFrame(() => requestAnimationFrame(() => setShown(true)));
      return () => cancelAnimationFrame(raf);
    }
    setShown(false);
    closeTimer.current = setTimeout(() => { setMounted(false); closeTimer.current = null; }, ANIM_MS);
    return () => { if (closeTimer.current) { clearTimeout(closeTimer.current); closeTimer.current = null; } };
  }, [open]);

  // Lock page scroll while the sheet is up (mirrors the prototype).
  useEffect(() => {
    if (!mounted) return;
    const prev = document.documentElement.style.overflow;
    document.documentElement.style.overflow = 'hidden';
    return () => { document.documentElement.style.overflow = prev; };
  }, [mounted]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!mounted) return null;

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={ariaLabel}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{
        position: 'fixed', inset: 0, zIndex: 2500,
        background: 'rgba(16,30,24,0.42)',
        display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
        opacity: shown ? 1 : 0, transition: `opacity ${ANIM_MS}ms`,
      }}
    >
      <div
        style={{
          position: 'relative', width: '100%', maxWidth: 480, maxHeight: '86%',
          background: '#ffffff', borderRadius: '24px 24px 0 0',
          boxShadow: '0 -12px 44px rgba(20,40,30,0.20)',
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
          transform: shown ? 'translateY(0)' : 'translateY(100%)',
          transition: `transform ${ANIM_MS}ms cubic-bezier(.32,.72,0,1)`,
        }}
      >
        <div aria-hidden style={{ width: 38, height: 4, borderRadius: 999, background: 'rgba(20,40,30,0.10)', margin: '10px auto 2px', flex: 'none' }} />
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          style={{
            position: 'absolute', top: 12, right: 14, width: 30, height: 30, zIndex: 2,
            border: 'none', background: '#f4f6f3', color: '#6c7872', borderRadius: '50%',
            fontSize: 14, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          ✕
        </button>
        <div style={{ overflowY: 'auto', minHeight: 0, padding: '6px 20px 24px' }}>{children}</div>
        {actions && (
          <div style={{ display: 'flex', gap: 10, padding: '12px 20px', borderTop: '1px solid rgba(20,40,30,0.06)', background: '#ffffff', flex: 'none' }}>
            {actions}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

export default BottomSheet;
